// sound5/master.js: the master chain and the hall.
//
//   dry + hall return -> tone (transition dip) -+
//   fx (one-shots that skip the dip) -----------+-> post -> duck (horizon silence)
//     -> highpass 24 Hz -> glue compressor -> limiter -> trim -> soft clip -> out -> speakers
//
// The limiter is a safety net (-4 dB threshold, ~2 dB auto make-up) rather than
// a constant squash; a 12 Hz DC blocker after it removes the offset its fast
// gain moves put on the sub drone. The soft clipper maps anything above
// -3.35 dBFS onto a tanh knee that never exceeds 0.84 (-1.5 dBFS), with oversampling off, so the output cannot pass
// -1 dBFS or clip whatever the voices do. DynamicsCompressorNode adds its own
// make-up gain (Web Audio spec), which `trim` takes back; levels are measured
// by tools/sound5_analyze.py, not guessed.
import { biquad, softClipCurve, chain, Knob } from './util.js';

// Hall length per tier: convolution cost grows with the impulse length, so
// phones and low-end machines get a shorter room.
export const HALL = { high: 4.2, mid: 2.6, low: 1.7 };

export class Master {
  constructor(ctx, { tier = 'high', dest } = {}) {
    this.ctx = ctx;
    const g = (v) => { const n = ctx.createGain(); n.gain.value = v; return n; };
    this.dry = g(1);
    this.send = g(1);
    this.fx = g(1);
    this.tone = biquad(ctx, 'lowpass', 18000, 0.5);
    this.post = g(1);
    this.duck = g(1);
    this.hp = biquad(ctx, 'highpass', 24, 0.7);
    this.comp = ctx.createDynamicsCompressor();
    this.lim = ctx.createDynamicsCompressor();
    this.trim = g(0.79);
    this.dcb = biquad(ctx, 'highpass', 12, 0.5);
    this.clip = ctx.createWaveShaper();
    this.out = g(0);
    const set = (node, o) => { for (const k in o) try { node[k].value = o[k]; } catch (e) { /* ignore */ } };
    // glue: slow and gentle, 2:1 over -22 dB with a wide knee
    set(this.comp, { threshold: -22, knee: 14, ratio: 2, attack: 0.04, release: 0.35 });
    // limiter: fast and hard; the 6 ms look-ahead of the node catches most overs
    set(this.lim, { threshold: -4, knee: 1, ratio: 20, attack: 0.003, release: 0.2 });
    this.clip.curve = softClipCurve(4097, 0.68, 0.84);
    this.clip.oversample = 'none';
    // the hall: high-passed and darkened send into a generated impulse
    // (convolution), or on 'low' a feedback-delay network that costs almost nothing
    this.sendHp = biquad(ctx, 'highpass', 150, 0.6);
    this.sendLp = biquad(ctx, 'lowpass', 6500, 0.5);
    this.wet = g(tier === 'low' ? 0.8 : 0.5);
    chain(this.send, this.sendHp, this.sendLp);
    if (tier === 'low') {
      fdnHall(ctx, this.sendLp, this.wet, HALL.low);
    } else {
      this.conv = ctx.createConvolver();
      try { this.conv.normalize = false; } catch (e) { /* ignore */ }
      chain(this.sendLp, this.conv, this.wet);
    }
    this.wet.connect(this.tone);
    this.dry.connect(this.tone);
    this.tone.connect(this.post);
    this.fx.connect(this.post);
    chain(this.post, this.duck, this.hp, this.comp, this.lim, this.trim, this.dcb, this.clip, this.out, dest || ctx.destination);
    this.hallSeconds = HALL[tier] || HALL.high;
    this.kTone = new Knob(this.tone.frequency, 18000, 0.03, 5);
    this.kPost = new Knob(this.post.gain, 1, 0.01, 1e-3);
    this.kDuck = new Knob(this.duck.gain, 1, 0.005, 1e-5);
    this.kOut = new Knob(this.out.gain, 0, 0.005, 1e-5);
  }

  // The impulse (the same recipe as util.makeIR) is generated a slice per
  // call from update(), outside the unlocking gesture, so no single frame pays
  // for all of it. Returns true once the hall is ready.
  hallStep(rng, slice = 0.6) {
    if (this.hall || !this.conv) return true;
    const ctx = this.ctx, sr = ctx.sampleRate, sec = this.hallSeconds;
    let h = this.ir;
    if (!h) {
      const len = Math.max(256, Math.floor(sr * sec));
      h = this.ir = { len, i: 0, d: [new Float32Array(len), new Float32Array(len)], st: [[0, 0, 1], [0, 0, 1]], e: 0 };
    }
    const pre = Math.floor(0.018 * sr), onset = 0.05 * sr, TWO_PI = Math.PI * 2;
    const dk = Math.exp(-6.9078 / (sec * 0.8 * sr)), end = Math.min(h.len, h.i + Math.floor(slice * sr));
    for (let ch = 0; ch < 2; ch++) {
      const d = h.d[ch], s = h.st[ch];
      let [l1, l2, env] = s, a = 0.5;
      for (let i = Math.max(h.i, pre); i < end; i++) {
        const n = i - pre;
        if ((n & 63) === 0 || i === h.i) a = 1 - Math.exp(-TWO_PI * (900 + 7600 * Math.exp(-(n / sr) / (sec * 0.18))) / sr);
        l1 += a * (rng() * 2 - 1 - l1);
        l2 += a * (l1 - l2);
        const v = l2 * env * (n < onset ? n / onset : 1);
        env *= dk;
        d[i] = v;
        h.e += v * v;
      }
      h.st[ch] = [l1, l2, env];
    }
    h.i = end;
    if (end < h.len) return false;
    const buf = ctx.createBuffer(2, h.len, sr), k = 1 / Math.sqrt(h.e / 2 || 1);
    for (let ch = 0; ch < 2; ch++) {
      const d = h.d[ch];
      for (let i = 0; i < h.len; i++) d[i] *= k;
      buf.getChannelData(ch).set(d);
    }
    this.conv.buffer = buf;
    this.ir = null;
    this.hall = true;
    return true;
  }

  // Transition dip: at the heart of a chapter cut the score passes through a
  // low-pass and drops 2.5 dB, under the whoosh.
  // `level` is the scene's overall level (the OFFER fades down with it).
  dip(tr, level, t) {
    const x = tr * tr;
    this.kTone.set(18000 * Math.pow(1500 / 18000, x), t, 0.06);
    this.kPost.set(level * (1 - 0.25 * x), t, 0.08);
  }

  dispose() {
    try { this.out.disconnect(); } catch (e) { /* ignore */ }
  }
}

// Four delay lines, Householder feedback matrix, a low-pass in each loop
// (darker as it decays), lines 0/2 to the left and 1/3 to the right.
function fdnHall(ctx, input, output, rt60) {
  const D = [0.0297, 0.0371, 0.0411, 0.0533];
  const merge = ctx.createChannelMerger(2);
  const lines = D.map((d) => {
    const dl = ctx.createDelay(0.1), lp = ctx.createBiquadFilter(), fb = ctx.createGain();
    // Web Audio lowpass Q is in dB: -3.01 dB is Butterworth (|H| <= 1). A positive
    // Q peaks above unity at the cutoff, the loop gain passes 1 and the hall
    // blows up to NaN within seconds (this silenced the whole 'low' tier).
    lp.type = 'lowpass'; lp.frequency.value = 4200; lp.Q.value = -3.01;
    try { lp.channelCount = 2; lp.channelCountMode = 'explicit'; } catch (e) { /* ignore */ }
    dl.delayTime.value = d;
    fb.gain.value = 0.97 * Math.pow(10, (-3 * d) / rt60);
    input.connect(dl);
    dl.connect(lp);
    lp.connect(fb);
    return { dl, fb };
  });
  lines.forEach((a, i) => {
    lines.forEach((b, j) => {
      const m = ctx.createGain();
      m.gain.value = i === j ? 0.5 : -0.5;
      a.fb.connect(m);
      m.connect(b.dl);
    });
    const o = ctx.createGain();
    o.gain.value = 0.5;
    a.fb.connect(o);
    o.connect(merge, 0, i % 2);
  });
  merge.connect(output);
}
